import { useEffect, useMemo, useState } from "react";
import "@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css";
import DatePicker from "@hassanmojab/react-modern-calendar-datepicker";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useAuth } from "../utils/auth";
import { sdkWrapperURL } from "../utils/api-url-list";
import LoadingScreen from "../components/LoadingScreen";

const formatDate = (date, type = "display") =>
  type === "api"
    ? `${date.year}-${date.month}-${date.day}`
    : `${date.day}/${date.month}/${date.year}`;

const Snapshot = () => {
  const {
    getToken,
    userData: { admin_type, client_isScanBased },
  } = useAuth();

  const [startDate, setStartDate] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: 1,
  });
  const [endDate, setEndDate] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
  });

  const [accountsList, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState({
    id: "",
    name: "",
    isScanBased: false,
  });
  const [chartData, setChartData] = useState([]);
  const [chartDataError, setChartDataError] = useState("");
  const [loading, setLoading] = useState(true);

  const dateRange = useMemo(() => {
    var today = new Date();
    const max = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
    today.setDate(today.getDate() - 90);
    const min = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
    return { min, max };
  }, []);

  useEffect(() => {
    const getAccountsList = async () => {
      setLoading(true);
      setAccounts([]);
      setSelectedAccount({ id: "", name: "", isScanBased: false });
      try {
        const token = await getToken();
        const listClientsResp = await fetch(sdkWrapperURL("/admin/analytics"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ action: "list_clients" }),
        });
        const listClientsRespJSON = await listClientsResp.json();
        if (listClientsRespJSON?.statusCode?.toString().startsWith("2")) {
          const accounts = listClientsRespJSON.profile.map((r) => ({
            id: r.client_id,
            name: r.display_name,
            isScanBased: r.client_configurator?.client_type === "SCAN_BASED",
          }));
          setAccounts(accounts);
          setSelectedAccount(accounts[0]);
        } else
          throw new Error(
            listClientsRespJSON?.message ?? "Failed to get Clients List"
          );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (admin_type === "SUPERADMIN") getAccountsList();
  }, [admin_type, getToken]);

  useEffect(() => {
    const getChartData = async (client_id) => {
      setLoading(true);
      setChartData([]);
      setChartDataError("");
      try {
        const start_date = formatDate(startDate, "api"),
          end_date = formatDate(endDate, "api");
        if (new Date(start_date) > new Date(end_date))
          throw new Error("Start date cannot be in future than End Date");
        const token = await getToken();
        const listInsightResp = await fetch(sdkWrapperURL("/admin/insights"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({
            analytics_inputs: { start_date, end_date },
            analytics_output_type: "RAW",
            client_id,
          }),
        });
        const listInsightRespJSON = await listInsightResp.json();
        if (listInsightRespJSON?.statusCode?.toString().startsWith("2"))
          setChartData(
            listInsightRespJSON.data.map((r) => ({
              Range: `${new Date(r.start_date).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
              })} - ${new Date(r.end_date).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
              })}`,
              "Total Scans": r.total_scans,
              "Successful Scans": r.successful_scans,
              "Failed Scans": r.failed_scans,
              "Incomplete Scans": r.incomplete_scans,
              "Newly Registered Users": r.newly_registered_user_count,
              "Users Initiated Scans": r.unique_user_total,
            }))
          );
        else
          throw new Error(
            listInsightRespJSON?.message ?? "Failed to get SnapShot Data"
          );
      } catch (err) {
        setChartDataError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (admin_type === "SUPERADMIN") {
      if (selectedAccount?.id?.length > 0) getChartData(selectedAccount.id);
    } else getChartData();
  }, [selectedAccount.id, startDate, endDate, admin_type, getToken]);

  const stats = useMemo(() => {
    const sum = chartData.reduce(
      (total, data) => {
        total.total_scans +=data["Total Scans"];
        total.successful_scans += data["Successful Scans"];
        total.failed_scans += data["Failed Scans"];
        total.registered_users += data["Newly Registered Users"];
        total.unique_user_total += data["Users Initiated Scans"];
        return total;
      },
      {
        successful_scans: 0,
        failed_scans: 0,
        total_scans: 0,
        incomplete_scans: 0,
        registered_users: 0,
        unique_user_total: 0,
      }
    );
    // const total_scans = sum.failed_scans + sum.successful_scans;
    return {
      ...sum,
      failure_rate: (sum.failed_scans / sum.total_scans) * 100,
    };
  }, [chartData]);

  return loading ? (
    <LoadingScreen style={{ minHeight: "60vh" }} />
  ) : (
    <div className="h-full flex flex-col items-stretch">
      <div className="shrink-0 flex justify-between">
        <div>
          <h1 className="text-primary text-2xl font-semibold">Snapshot</h1>
          <p className="mt-4 text-darkgray text-sm">
            Here is the latest snapshot of your account.
          </p>
        </div>
        <div>
          <div className="flex justify-between gap-5">
            {admin_type === "SUPERADMIN" && (
              <select
                className="block w-40 p-2.5 rounded-lg outline-none border border-lightgray/50 bg-white text-primary text-sm"
                id="select-report"
                name="select-report-name"
                value={selectedAccount.id}
                onChange={(e) =>
                  setSelectedAccount(
                    accountsList.find((a) => a.id === e.target.value)
                  )
                }
              >
                <option value="" hidden disabled>
                  Select Account
                </option>
                {accountsList.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            )}
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              formatInputText={() => formatDate(startDate)}
              inputName="start-date"
              inputPlaceholder="Start Date"
              inputClassName="date-input block w-40 !p-2.5 rounded-lg outline-none !border !border-lightgray/50 text-primary !text-sm !text-left"
              wrapperClassName="block"
              shouldHighlightWeekends
              calendarPopperPosition="bottom"
              minimumDate={dateRange.min}
              maximumDate={dateRange.max}
            />
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              formatInputText={() => formatDate(endDate)}
              inputName="end-date"
              inputPlaceholder="End Date"
              inputClassName="date-input block w-40 !p-2.5 rounded-lg outline-none !border !border-lightgray/50 text-primary !text-sm !text-left"
              wrapperClassName="block"
              shouldHighlightWeekends
              calendarPopperPosition="bottom"
              minimumDate={dateRange.min}
              maximumDate={dateRange.max}
            />
          </div>
          {chartDataError.length > 0 && (
            <p className="mt-1.5 text-sm text-error text-right">
              {chartDataError}
            </p>
          )}
        </div>
      </div>
      <div className="grow mt-8 grid grid-cols-4 gap-8">
        <div className="order-1 p-6 rounded-xl border-2 border-secondary text-center flex flex-col items-center justify-center">
          <h2 className="text-primary text-4xl font-semibold">
            {stats.total_scans || "--"}
          </h2>
          <p className="text-secondary text-sm">Total Scans Initiated</p>
        </div>
        <div className="order-2 p-6 rounded-xl border-2 border-[#64BD40] text-center flex flex-col items-center justify-center">
          <h2 className="text-primary text-4xl font-semibold">
            {stats.successful_scans || "--"}
          </h2>
          <p className="text-secondary text-sm">Successful Scans Completed</p>
        </div>
        <div className="order-3 p-6 rounded-xl border-2 border-[#FF0000] text-center flex flex-col items-center justify-center">
          <h2 className="text-primary text-4xl font-semibold">
            {stats.failed_scans || "--"}
          </h2>
          <p className="text-secondary text-sm">Failed Scans Encountered</p>
        </div>
        {/* <div className="order-3 p-6 rounded-xl border-2 border-[#FF0000] text-center flex flex-col items-center justify-center">
          <h2 className="text-primary text-4xl font-semibold">
            {stats.failed_scans || "--"}
          </h2>
          <p className="text-secondary text-sm">Incomplete Scans</p>
        </div> */}
        {!(admin_type === "SUPERADMIN"
          ? selectedAccount.isScanBased
          : client_isScanBased) && (
          <div className="order-4 p-6 rounded-xl border-2 border-[#F9A038] text-center flex flex-col items-center justify-center">
            <h2 className="text-primary text-4xl font-semibold ">
              {stats.registered_users || "--"}
            </h2>
            <p className="text-secondary text-sm">
              Total Newly Registered Users
            </p>
          </div>
        )}
        <ResponsiveContainer className="order-5 col-span-3 max-3xl:min-h-60 p-6 rounded-2xl border-2 border-secondary">
          <LineChart
            data={chartData}
            margin={{ top: 5, bottom: 0, right: 20, left: -20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#aaa" />
            <XAxis
              type="category"
              dataKey="Range"
              tickLine={false}
              tickMargin={12}
              tickFormatter={(_, i) => `Week ${i + 1}`}
              fontSize="12"
            />
            <YAxis
              type="number"
              allowDecimals={false}
              domain={[0, "dataMax"]}
              tickSize={5}
              tickMargin={5}
              fontSize="12"
            />
            <Line type="monotone" dataKey="Successful Scans" stroke="#64BD40" />
            <Line type="monotone" dataKey="Failed Scans" stroke="#FF0000" />
            {!(admin_type === "SUPERADMIN"
              ? selectedAccount.isScanBased
              : client_isScanBased) && (
              <Line
                type="monotone"
                dataKey="Newly Registered Users"
                stroke="#F9A038"
              />
            )}
            {stats.unique_user_total > 0 && (
              <Line
                type="monotone"
                dataKey="Users Initiated Scans"
                stroke="#2adabc"
              />
            )}
            <Tooltip wrapperStyle={{ zIndex: 100 }} />
          </LineChart>
        </ResponsiveContainer>
        <div
          className={`order-6 flex ${
            stats.unique_user_total > 0
              ? "flex-col gap-8"
              : "items-center justify-center"
          }`}
        >
          {stats.unique_user_total > 0 && (
            <div className="p-6 rounded-xl border-2 border-[#2adabc] text-center flex flex-col items-center justify-center">
              <h2 className="text-primary text-4xl font-semibold">
                {stats.unique_user_total}
              </h2>
              <p className="text-secondary text-sm">Users Initiated Scans</p>
            </div>
          )}
          <div
            className={`${
              !(admin_type === "SUPERADMIN"
                ? selectedAccount.isScanBased
                : client_isScanBased)
                ? "order-6"
                : "order-4"
            } text-center flex flex-col items-center justify-center`}
          >
            <h2 className="text-primary text-4xl font-semibold">
              {isNaN(stats.failure_rate)
                ? "--"
                : stats.failure_rate.toFixed(2) + "%"}
            </h2>
            <p className="text-secondary text-sm">Failure Rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Snapshot;
